import {z} from 'zod';

import {isNil} from 'ramda';

import {
  UuidResponseBody,
  Money,
  MoneySchema,
  PhoneNumber,
  BigDecimal,
  BigDecimalSchema,
  AdvertisingPlatformCodeEnum,
  TaskTypeEnumSchema,
  TaskPriorityEnumSchema,
  TaskStateEnumSchema,
} from '@dms/api/shared';

export type UpdateVehicleIsInSaleApiArg = {
  vehicleId: string;
  publish: boolean;
  validateMandatoryFields?: boolean;
  publishMobileDeViaAdvertisingBridge?: boolean;
  publishVgpViaAdvertisingBridge?: boolean;
  publishOtomotoViaAdvertisingBridge?: boolean;
  publishAutocarisViaAdvertisingBridge?: boolean;
  publishMercedesNaSkladeViaAdvertisingBridge?: boolean;
  publishHyundaiPromiseViaAdvertisingBridge?: boolean;
};

export type UpdateVehicleIsInSaleApiRes = unknown;

export type GetSalesPlatformsApiArg = {vehicleId: string};

export enum AdvertisementStatusEnum {
  NOT_PUBLISHED = 'not_published',
  PUBLISHING = 'publishing',
  PUBLISH_FAILED = 'publish_failed',
  PUBLISHED = 'published',
  UPDATING = 'updating',
  UPDATE_FAILED = 'update_failed',
  UNPUBLISHING = 'unpublishing',
  UNPUBLISH_FAILED = 'unpublish_failed',
  UNKNOWN = 'unknown',
}

export type SalesPlatformsResBody = {
  enabled: boolean;
  platform: {
    code: AdvertisingPlatformCodeEnum;
    title: string;
    enabled: boolean;
    branchId: string | null;
    // Usable extension length for this vehicle on this platform. The BE already
    // subtracts the composed title budget where the platform receives
    // `title + ' ' + extension` as one string, and serves the conservative fallback
    // envelope for platforms whose real limit is unverified.
    titleExtensionLimit: number;
    // False when the platform's real limit is unverified and the effective limit
    // is the fallback.
    titleExtensionLimitVerified: boolean;
    // Publish-time subtitle limit, null when the platform has no subtitle.
    subtitleLimit: number | null;
  };
  url: string | null;
  status: AdvertisementStatusEnum;
  publicationStatus?:
    | 'pending-approval'
    | 'published'
    | 'rejected'
    | 'withdrawn'
    | 'unknown'
    | null;
  statusDescription: string | null;
  statusTriggeredAt: string | null;
  statusTriggeredBy: string | null;
  priceWithVat: {
    amount: string;
    currency: string;
  } | null;
  publishingBranchId: string | null;
  publishingBranchName: string | null;
  hasOverrides: boolean;
};
export type GetSalesPlatformsApiRes = Array<SalesPlatformsResBody>;

const AdvertisementPlatformCodeSchema = z.nativeEnum(AdvertisingPlatformCodeEnum);

const AdvertisementPreflightIssueSchema = z.object({
  kind: z.enum(['field', 'vehicle-taxonomy', 'equipment-taxonomy', 'configuration']),
  reasonCode: z.string(),
  mappingIdentifier: z.string().nullable(),
  targetPath: z.string().nullable(),
  sourcePaths: z.array(z.string()),
  blockingDimension: z.string().nullable(),
});

export const AdvertisementPreflightStatusSchema = z.enum(['ready', 'blocked']);

export const GetAdvertisementPreflightApiResponseSchema = z.object({
  results: z.array(
    z.object({
      platformCode: AdvertisementPlatformCodeSchema,
      status: AdvertisementPreflightStatusSchema,
      issues: z.array(AdvertisementPreflightIssueSchema),
    })
  ),
});

export type AdvertisementPreflightIssue = z.infer<typeof AdvertisementPreflightIssueSchema>;
export type AdvertisementPreflightStatus = z.infer<typeof AdvertisementPreflightStatusSchema>;
export type GetAdvertisementPreflightApiArg = {
  vehicleId: string;
  publishVgpViaAdvertisingBridge?: boolean;
  publishOtomotoViaAdvertisingBridge?: boolean;
  publishAutocarisViaAdvertisingBridge?: boolean;
  publishMercedesNaSkladeViaAdvertisingBridge?: boolean;
  publishHyundaiPromiseViaAdvertisingBridge?: boolean;
};
export type GetAdvertisementPreflightApiResponse = z.infer<
  typeof GetAdvertisementPreflightApiResponseSchema
>;

export type GetAdvertisementPreviewApiArg = {
  vehicleId: string;
  platformCode?: AdvertisingPlatformCodeEnum;
  language?: string;
};

export type GetAdvertisementPreviewPhotosApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
};

const AdvertisementPreviewLimitedTextSchema = z.object({
  value: z.string().nullable(),
  length: z.number(),
  limit: z.number().nullable(),
  limitVerified: z.boolean(),
  state: z.enum(['fits', 'over_limit', 'dropped', 'unknown']),
});

const AdvertisementPreviewPriceResponseBodySchema = z.object({
  amount: z.string(),
  currency: z.string(),
  withoutVat: MoneySchema.nullable(),
  isVatDeductible: z.boolean(),
});
export type AdvertisementPreviewPriceResponseBody = z.infer<
  typeof AdvertisementPreviewPriceResponseBodySchema
>;

const AdvertisementPreviewPhotoSchema = z.object({
  fileId: z.string(),
  url: z.string(),
  is360: z.boolean(),
  isPromo: z.boolean(),
  isCover: z.boolean(),
});

export const GetAdvertisementPreviewPhotosApiResponseSchema = z.object({
  platformCode: AdvertisementPlatformCodeSchema,
  photos: z.array(AdvertisementPreviewPhotoSchema),
  issues: z.array(
    z.object({
      severity: z.literal('error'),
      code: z.literal('watermark_file_missing'),
      target: z.literal('watermark_settings'),
      platformCode: AdvertisementPlatformCodeSchema.nullable(),
      details: z
        .object({
          watermarkIds: z.array(z.string().uuid()),
          fileIds: z.array(z.string().uuid()),
        })
        .nullable(),
    })
  ),
});
export type GetAdvertisementPreviewPhotosApiResponse = z.infer<
  typeof GetAdvertisementPreviewPhotosApiResponseSchema
>;
export type AdvertisementPreviewPhoto = GetAdvertisementPreviewPhotosApiResponse['photos'][number];

export const GetAdvertisementPreviewApiResponseSchema = z.object({
  title: z.string(),
  titleExtension: z.string().nullable(),
  description: z.string().nullable(),
  vehicle: z.object({
    firstRegistrationOn: z.string().nullable(),
    fuelType: z.string().nullable(),
    power: z.number().nullable(),
    transmission: z.string().nullable(),
    exteriorColor: z.string().nullable(),
  }),
  language: z.string(),
  languages: z.array(
    z.object({
      language: z.string(),
      isDefault: z.boolean(),
      published: z.boolean(),
    })
  ),
  canPublish: z.boolean(),
  platforms: z.array(
    z.object({
      platformCode: AdvertisementPlatformCodeSchema,
      enabled: z.boolean(),
      status: z.nativeEnum(AdvertisementStatusEnum),
      titleExtension: AdvertisementPreviewLimitedTextSchema,
      description: AdvertisementPreviewLimitedTextSchema.extend({
        format: z.enum([
          'html',
          'plain_text',
          'markdown',
          'mobile_de_markup',
          'autoscout24_markup',
        ]),
      }),
      price: AdvertisementPreviewPriceResponseBodySchema.nullable(),
      mileage: z.number().int().nullable(),
      vin: z.string().nullable(),
      vinHidden: z.boolean(),
    })
  ),
});

export type GetAdvertisementPreviewApiResponse = z.infer<
  typeof GetAdvertisementPreviewApiResponseSchema
>;
export type AdvertisementPreviewPlatform = GetAdvertisementPreviewApiResponse['platforms'][number];

export type VehicleAdAttributeApiArg = {vehicleId: string};
export type GetVehicleAdAttributesApiRes = {
  version: AdvertisingPlatformCodeEnum[];
  generation: AdvertisingPlatformCodeEnum[];
  highlights: AdvertisingPlatformCodeEnum[];
  modelRange: AdvertisingPlatformCodeEnum[];
  trimLine: AdvertisingPlatformCodeEnum[];
  registrationPlate: AdvertisingPlatformCodeEnum[];
  subtitle: AdvertisingPlatformCodeEnum[];
};
export type MobileDeRefdataOptionsResponseBody = {options: string[]; suggestion: string | null};

export type UpdateVehicleOnAllPlatformsApiResponse = unknown;
export type UpdateVehicleOnAllPlatformsApiArg = {
  vehicleId: string;
};
export type UpdateVehicleOnPlatformApiResponse = unknown;
export type UpdateVehicleOnPlatformApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
};
export type EditGeneralSettingsApiResponse =
  /** status 200  */ VehicleAdvertisingSettingsResponseBody;
export type EditGeneralSettingsApiArg = {
  vehicleId: string;
  patchVehicleAdvertisingSettingsRequestBody: PatchVehicleAdvertisingSettingsRequestBody;
};
export type GetGeneralSettingsApiResponse =
  /** status 200  */ VehicleAdvertisingSettingsResponseBody;
export type GetGeneralSettingsApiArg = {
  vehicleId: string;
};
export type EditWindshieldSettingsApiResponse =
  /** status 200  */ VehicleWindshieldAdvertisingSettingsResponseBody;
export type EditWindshieldSettingsApiArg = {
  vehicleId: string;
  patchVehicleWindshieldAdvertisingSettingsRequestBody: PatchVehicleWindshieldAdvertisingSettingsRequestBody;
};
export type GetWindshieldSettingsApiResponse =
  /** status 200  */ VehicleWindshieldAdvertisingSettingsResponseBody;
export type GetWindshieldSettingsApiArg = {
  vehicleId: string;
};
export type VehicleAdvertisingSettingsCodeEnum =
  | 'title_extension'
  | 'model_info'
  | 'vin'
  | 'registration_plate'
  | 'mileage'
  | 'price'
  | 'description'
  | 'price_note'
  | 'financing_price'
  | 'contact_email'
  | 'contact_phone'
  | 'url'
  | 'condition'
  | 'version'
  | 'generation'
  | 'digital_certificate'
  | 'digital_certificate_theme'
  | 'video_url'
  | 'highlights'
  | 'model_range'
  | 'trim_line'
  | 'subtitle';
export type VehicleAdvertisingSettingsTypeEnum =
  | 'string'
  | 'text'
  | 'mileage'
  | 'price'
  | 'financing_price'
  | 'email'
  | 'phone'
  | 'condition'
  | 'digital_certificate'
  | 'digital_certificate_theme';
export type CommonTitleExtensionAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonRegistrationPlateAttribute = {
  code: 'registration_plate';
  type?: 'string';
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra: {hidden: boolean};
  required?: false;
  override: false;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonVinAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: true;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type MileageValue = {
  value: number;
  unit: 'km';
};
export type CommonMileageAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: MileageValue | null;
  defaultValue?: MileageValue | null;
  displayValue?: MileageValue | null;
  extra?: object | null;
  required?: boolean;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type PriceValue = {
  vatDeductible?: boolean;
  vatRate?: BigDecimal | null;
  value: Money;
  withVat: boolean;
  vatCode?: string | null;
  countryCode?: string;
};
export type PriceExtra = {
  vatDeductible: boolean;
  vatRate: BigDecimal | null;
  vatCode: string;
  countryCode: string;
};
export type CommonPriceAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: PriceValue | null;
  defaultValue?: PriceValue | null;
  displayValue?: PriceValue | null;
  extra?: PriceExtra;
  required?: boolean;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonFinancingPriceAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: PriceValue | null;
  defaultValue?: PriceValue | null;
  displayValue?: PriceValue | null;
  extra?: PriceExtra;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonPriceNoteAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonDescriptionAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonVersionAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonGenerationAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonDigitalCertificateAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonDigitalCertificateThemeAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonVideoUrlAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonConditionAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: number | null;
  defaultValue?: number | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonHighlightsAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: (string | null)[] | null;
  defaultValue?: (string | null)[] | null;
  displayValue?: (string | null)[] | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonModelRangeAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonTrimLineAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type CommonSubtitleAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type VehicleGeneralAdvertisingAttributeResponseBody =
  | CommonTitleExtensionAttribute
  | CommonSubtitleAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute
  | CommonVersionAttribute
  | CommonGenerationAttribute
  | CommonDigitalCertificateAttribute
  | CommonDigitalCertificateThemeAttribute
  | CommonVideoUrlAttribute
  | CommonConditionAttribute;
export type VehicleGeneralAdvertisingSettingsResponseBody = {
  attributes: VehicleGeneralAdvertisingAttributeResponseBody[];
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehicleSAutoPlatformAdvertisingAttributeResponseBody =
  | CommonRegistrationPlateAttribute
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute;
export type VehicleSAutoPlatformAdvertisingSettingsResponseBody = {
  platformCode: AdvertisingPlatformCodeEnum;
  enabled: boolean;
  attributes: VehicleSAutoPlatformAdvertisingAttributeResponseBody[];
  hasOverrides: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
export type TipCarsPriceNoteAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type VehicleTipCarsPlatformAdvertisingAttributeResponseBody =
  | CommonRegistrationPlateAttribute
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | TipCarsPriceNoteAttribute
  | CommonDescriptionAttribute;
export type VehicleTipCarsPlatformAdvertisingSettingsResponseBody = {
  platformCode: AdvertisingPlatformCodeEnum;
  enabled: boolean;
  attributes: VehicleTipCarsPlatformAdvertisingAttributeResponseBody[];
  hasOverrides: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehicleMobileDePlatformAdvertisingAttributeResponseBody =
  | CommonRegistrationPlateAttribute
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute;
export type VehicleMobileDePlatformAdvertisingSettingsResponseBody = {
  platformCode: AdvertisingPlatformCodeEnum;
  enabled: boolean;
  attributes: VehicleMobileDePlatformAdvertisingAttributeResponseBody[];
  hasOverrides: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehicleMyWebPlatformAdvertisingAttributeResponseBody =
  | CommonRegistrationPlateAttribute
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute;
export type VehicleMyWebPlatformAdvertisingSettingsResponseBody = {
  platformCode: AdvertisingPlatformCodeEnum;
  enabled: boolean;
  attributes: VehicleMyWebPlatformAdvertisingAttributeResponseBody[];
  hasOverrides: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehicleFacebookPlatformAdvertisingAttributeResponseBody =
  | CommonRegistrationPlateAttribute
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute;
export type VehicleFacebookPlatformAdvertisingSettingsResponseBody = {
  platformCode: AdvertisingPlatformCodeEnum;
  enabled: boolean;
  attributes: VehicleFacebookPlatformAdvertisingAttributeResponseBody[];
  hasOverrides: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehicleAutocarisPlatformAdvertisingAttributeResponseBody =
  | CommonRegistrationPlateAttribute
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute;
export type VehicleAutocarisPlatformAdvertisingSettingsResponseBody = {
  platformCode: AdvertisingPlatformCodeEnum;
  enabled: boolean;
  attributes: VehicleAutocarisPlatformAdvertisingAttributeResponseBody[];
  hasOverrides: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehiclePlatformAdvertisingSettingsResponseBody = (
  | VehicleSAutoPlatformAdvertisingSettingsResponseBody
  | VehicleTipCarsPlatformAdvertisingSettingsResponseBody
  | VehicleMobileDePlatformAdvertisingSettingsResponseBody
  | VehicleMyWebPlatformAdvertisingSettingsResponseBody
  | VehicleFacebookPlatformAdvertisingSettingsResponseBody
  | VehicleAutocarisPlatformAdvertisingSettingsResponseBody
) & {platformInput?: AutomarketplPlatformInput | null};
export type VehicleAdvertisingSettingsResponseBody = {
  vehicleId: string;
  inSale: boolean;
  general: VehicleGeneralAdvertisingSettingsResponseBody;
  platforms?: VehiclePlatformAdvertisingSettingsResponseBody[];
};
export type VehicleGeneralAdvertisingAttributeRequestBody =
  | CommonTitleExtensionAttribute
  | CommonSubtitleAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | CommonDescriptionAttribute
  | CommonVersionAttribute
  | CommonGenerationAttribute
  | CommonConditionAttribute
  | CommonHighlightsAttribute
  | CommonModelRangeAttribute
  | CommonTrimLineAttribute;
export type VehicleGeneralAdvertisingSettingsRequestBody = {
  attributes: VehicleGeneralAdvertisingAttributeRequestBody[];
};
export type PatchVehicleAdvertisingSettingsRequestBody = {
  general?: VehicleGeneralAdvertisingSettingsRequestBody | null;
};
export type VehiclePlatformAdvertisingAttributeRequestBody =
  | CommonTitleExtensionAttribute
  | CommonVinAttribute
  | CommonMileageAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | TipCarsPriceNoteAttribute
  | CommonDescriptionAttribute;

const AUTOMARKETPL_TIRE_SIZE_PATTERN = /^\d{3}\/\d{2} [RDB]\d{2}(?: [A-Z])?$/;

export const AutomarketplTireSizeSchema: z.ZodType<string | undefined, z.ZodTypeDef, unknown> =
  z.preprocess((value) => {
    if (isNil(value)) {
      return undefined;
    }
    if (typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }, z.string().regex(AUTOMARKETPL_TIRE_SIZE_PATTERN).optional());

export const AutomarketplPlatformInputSchema = z
  .object({
    rozmiarOgumienia: AutomarketplTireSizeSchema,
    inspekcja: z
      .array(
        z
          .object({
            pytanieId: z.number().int().safe().positive(),
            wartosc: z.union([z.literal(0), z.literal(1)]),
            uwagi: z.string().optional(),
          })
          .strict()
      )
      .max(1000)
      .optional(),
    zalacznikSpecyfikacja: z.string().url().optional(),
    kodEurotax: z.number().int().safe().positive().optional(),
  })
  .strict();

export const AutomarketplInspectionQuestionsSchema = z.object({
  inspectionQuestions: z.array(
    z.object({
      pytanieId: z.number().int(),
      kategoriaId: z.number().int(),
      trescKrotka: z.string(),
      trescDluga: z.string(),
      podpowiedz: z.string(),
      dyskwalifikacja: z.number().int(),
    })
  ),
});
export type AutomarketplInspectionQuestions = z.infer<typeof AutomarketplInspectionQuestionsSchema>;

export type AutomarketplPlatformInput = z.infer<typeof AutomarketplPlatformInputSchema>;

export type VehiclePlatformAdvertisingSettingsRequestBody = {
  attributes: VehiclePlatformAdvertisingAttributeRequestBody[];
  platformInput?: AutomarketplPlatformInput | null;
};
export type GetBusinessCaseDocumentsSettingsApiResponse =
  /** status 200  */ BusinessCaseDocumentsSettingsResponseBody;
export type PutBusinessCaseDocumentsSettingsApiResponse = /** status 204  */ undefined;
export type PutBusinessCaseDocumentsSettingsApiArg = {
  businessCaseDocumentsSettingsRequestBody: BusinessCaseDocumentsSettingsRequestBody;
};
export type SellingBusinessCaseDocumentsResponseBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  saleHandoverProtocol: boolean;
  saleContract: boolean;
  orderForm: boolean;
  consent: boolean;
};
export type BuyingBusinessCaseDocumentsResponseBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  handoverProtocol: boolean;
  purchaseContract: boolean;
  consent: boolean;
};
export type PurchaseBrokerageBusinessCaseDocumentsResponseBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  purchaseBrokerageHandoverProtocol: boolean;
  purchaseBrokerageContract: boolean;
  consent: boolean;
};
export type SellingBrokerageBusinessCaseDocumentsResponseBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  saleHandoverProtocol: boolean;
  saleBrokerageContract: boolean;
  consent: boolean;
};
export type BusinessCaseDocumentsSettingsResponseBody = {
  selling: SellingBusinessCaseDocumentsResponseBody;
  buying: BuyingBusinessCaseDocumentsResponseBody;
  purchaseBrokerage: PurchaseBrokerageBusinessCaseDocumentsResponseBody;
  sellingBrokerage: SellingBrokerageBusinessCaseDocumentsResponseBody;
};
export type SellingBusinessCaseDocumentsRequestBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  saleHandoverProtocol: boolean;
  saleContract: boolean;
  orderForm: boolean;
  consent: boolean;
};
export type BuyingBusinessCaseDocumentsRequestBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  handoverProtocol: boolean;
  purchaseContract: boolean;
  consent: boolean;
};
export type PurchaseBrokerageBusinessCaseDocumentsRequestBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  purchaseBrokerageHandoverProtocol: boolean;
  purchaseBrokerageContract: boolean;
  consent: boolean;
};
export type SellingBrokerageBusinessCaseDocumentsRequestBody = {
  vehicleCondition: boolean;
  powerOfAttorney: boolean;
  saleHandoverProtocol: boolean;
  saleBrokerageContract: boolean;
  consent: boolean;
};
export type BusinessCaseDocumentsSettingsRequestBody = {
  selling: SellingBusinessCaseDocumentsRequestBody;
  buying: BuyingBusinessCaseDocumentsRequestBody;
  purchaseBrokerage: PurchaseBrokerageBusinessCaseDocumentsRequestBody;
  sellingBrokerage: SellingBrokerageBusinessCaseDocumentsRequestBody;
};
export type CreatePurchaseBusinessCaseApiResponse = /** status 201  */ UuidResponseBodyBusinessCase;
export type CreatePurchaseBusinessCaseApiArg = {
  createPurchaseBusinessCaseRequestBody: CreatePurchaseBusinessCaseRequestBody;
};
export const SalesActivityRelatedTaskResponseBodySchema = z.object({
  id: z.string(),
  title: z.string(),
  priority: TaskPriorityEnumSchema,
  type: TaskTypeEnumSchema,
  state: TaskStateEnumSchema,
  assignedTo: z.string(),
  dueDate: z.string().nullable(),
  dueFrom: z.string().nullable(),
  dueTo: z.string().nullable(),
  finishedAt: z.string().nullable(),
  description: z.string().nullable(),
});
export type SalesActivityRelatedTaskResponseBody = z.infer<
  typeof SalesActivityRelatedTaskResponseBodySchema
>;
export const SalesActivityTypeEnumSchema = z.enum([
  'INTEREST_CREATED',
  'INTEREST_CLOSED',
  'INTEREST_OPENED',
  'INTEREST_OPENED_ON_CUSTOMER_CONTACT',
  'INTEREST_REOPENED',
  'BUSINESS_CASE_CREATED',
  'BUSINESS_CASE_CLOSED',
  'CONTRACT_CLOSED',
  'BUSINESS_CASE_OPPORTUNITY_LOST',
  'INTEREST_OPPORTUNITY_LOST',
  'DEPOSIT_PAYMENT_ISSUED',
  'BALANCE_PAYMENT_ISSUED',
  'PURCHASE_PAYMENT_ISSUED',
  'PAYMENT_PAID',
  'TASK_CREATED',
  'TASK_FINISHED',
  'ONLINE_OFFER_SHARING_CANCELED',
  'ONLINE_OFFER_SENT',
  'ONLINE_OFFER_EMAIL_OPENED',
  'BUYING_ONLINE_OFFER_SHARING_CANCELED',
  'BUYING_ONLINE_OFFER_SENT',
  'BUYING_ONLINE_OFFER_EMAIL_OPENED',
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
  'EMAIL_TO_CUSTOMER',
  'SMS_TO_CUSTOMER',
  'INBOUND_CALL',
  'OUTBOUND_CALL',
  'CONTRACT_CANCELED',
  'BUSINESS_CASE_REOPENED',
  'DEPOSIT_PAYMENT_CANCELED',
  'DEPOSIT_PAYMENT_WITHDRAWN',
  'BALANCE_PAYMENT_CANCELED',
  'BALANCE_PAYMENT_WITHDRAWN',
  'PURCHASE_PAYMENT_CANCELED',
  'PURCHASE_PAYMENT_WITHDRAWN',
  'FULL_PAYMENT_CORRECTIVE_DOCUMENT_CREATED',
  'PDF_OFFER_SENT',
  'CHAT_WITH_CUSTOMER',
  'TEST_DRIVE_PLANNED',
  'TEST_DRIVE_CANCELLED',
  'TEST_DRIVE_COMPLETED',
  'TEST_DRIVE_DELETED',
  'THANK_YOU_AFTER_SALE_SENT',
  'THANK_YOU_AFTER_SALE_SKIPPED_NO_CONTACT',
  'THANK_YOU_AFTER_SALE_SKIPPED_CUSTOMER_REFUSED',
  'AUCTION_PUBLISHED',
  'AUCTION_OFFER_RECEIVED',
  'AUCTION_BIDDING_CLOSED',
  'AUCTION_WINNER_CONFIRMED',
  'AUCTION_TRANSACTION_STATUS_CHANGED',
  'CHECK_IN_AFTER_SALE_CANCELLED_REOPENED',
  'CHECK_IN_AFTER_SALE_CANCELLED_UNSUCCESSFUL',
  'CHECK_IN_AFTER_SALE_SENT',
  'CHECK_IN_AFTER_SALE_SKIPPED_NO_CONTACT',
  'CHECK_IN_AFTER_SALE_SKIPPED_CUSTOMER_REFUSED',
  'CHECK_IN_AFTER_SALE_SKIPPED_ALREADY_RATED',
  'CHECK_IN_AFTER_SALE_SKIPPED_DISABLED',
  'CHECK_IN_AFTER_SALE_SKIPPED_ONLINE_OFFER_UNAVAILABLE',
  'CUSTOMER_RATED_PURCHASE',
  'CUSTOMER_REFUSED_COMMUNICATION',
  'AUTOMATIC_CUSTOMER_MESSAGE_SENT',
  'AUTOMATIC_CUSTOMER_MESSAGE_SENT_WITHOUT_DOCUMENT',
  'AUTOMATIC_CUSTOMER_MESSAGE_SKIPPED_NO_CONTACT',
  'AUTOMATIC_CUSTOMER_MESSAGE_SKIPPED_CUSTOMER_REFUSED',
]);
export type SalesActivityTypeEnum = z.infer<typeof SalesActivityTypeEnumSchema>;

export type DeleteSalesActivityApiResponse = /** status 200  */ object;
export type DeleteSalesActivityApiArg = {
  salesActivityId: string;
};

export const SalesActivityRelatedCallResponseBodySchema = z.object({
  id: z.string(),
});
export type SalesActivityRelatedCallResponseBody = z.infer<
  typeof SalesActivityRelatedCallResponseBodySchema
>;
export type PatchSalesActivityApiResponse = /** status 204  */ void;
export type PatchSalesActivityApiArg = {
  salesActivityId: string;
  patchSalesActivityRequestBody: PatchSalesActivityRequestBody;
};
export const SalesActivityRelatedMessageResponseBodySchema = z.object({
  id: z.string(),
  body: z.string(),
  subject: z.string().nullable().optional(),
  sentTo: z.string().nullable().optional(),
  attachments: z
    .array(
      z.object({
        filename: z.string(),
        uri: z.string(),
        contentType: z.string().nullable().optional(),
        size: z.number().nullable().optional(),
      })
    )
    .nullable()
    .optional(),
  sender: z.object({
    name: z.string(),
    email: z.string(),
  }),
});
export type SalesActivityRelatedMessageResponseBody = z.infer<
  typeof SalesActivityRelatedMessageResponseBodySchema
>;
export const SalesActivityRelatedChatConversationResponseBodySchema = z.object({
  id: z.string(),
});
export type SalesActivityRelatedChatConversationResponseBody = z.infer<
  typeof SalesActivityRelatedChatConversationResponseBodySchema
>;
export const SalesActivityRelatedTestDriveResponseBodySchema = z.object({
  id: z.string(),
  vehicleId: z.string(),
  userId: z.string(),
  returnedAt: z.string().nullable(),
  scheduledAt: z.string(),
});
export type SalesActivityRelatedTestDriveResponseBody = z.infer<
  typeof SalesActivityRelatedTestDriveResponseBodySchema
>;
export const BusinessCaseActivityOnlineOfferEngagementResponseBodySchema = z.object({
  activeTimeMs: z.number(),
  maxScrollDepth: z.union([
    z.literal(0),
    z.literal(25),
    z.literal(50),
    z.literal(75),
    z.literal(100),
  ]),
});
export type BusinessCaseActivityOnlineOfferEngagementResponseBody = z.infer<
  typeof BusinessCaseActivityOnlineOfferEngagementResponseBodySchema
>;
export const BusinessCaseActivityCustomerRatingResponseBodySchema = z.object({
  rating: z.number().int().min(1).max(5),
  recommendation: z.number().int().min(0).max(10).nullable(),
  hasComment: z.boolean(),
});
export type BusinessCaseActivityCustomerRatingResponseBody = z.infer<
  typeof BusinessCaseActivityCustomerRatingResponseBodySchema
>;
export const BusinessCaseActivityResponseBodySchema = z.object({
  id: z.string(),
  customerMessageId: z.string().nullable().optional(),
  relatedResourceId: z.string().nullable().optional(),
  interestId: z.string().nullable(),
  interestCode: z.string().nullable(),
  interestType: z.string().nullable(),
  subject: z.string().nullable(),
  activityType: SalesActivityTypeEnumSchema,
  userId: z.string().nullable(),
  userName: z.string(),
  timestamp: z.string(),
  relatedTask: SalesActivityRelatedTaskResponseBodySchema.nullable(),
  relatedMessage: SalesActivityRelatedMessageResponseBodySchema.nullable(),
  description: z.string().nullable(),
  reasonCloseCodeId: z.string().nullable(),
  reasonCloseNote: z.string().nullable(),
  relatedCall: SalesActivityRelatedCallResponseBodySchema.nullable(),
  isEditable: z.boolean(),
  relatedChatConversation: SalesActivityRelatedChatConversationResponseBodySchema.nullable(),
  relatedTestDrive: SalesActivityRelatedTestDriveResponseBodySchema.nullable(),
  onlineOfferEngagement: BusinessCaseActivityOnlineOfferEngagementResponseBodySchema.nullable(),
  // The backend sends null when absent; optional keeps existing activity fixtures compatible.
  customerRating: BusinessCaseActivityCustomerRatingResponseBodySchema.nullable().optional(),
});
export type BusinessCaseActivityResponseBody = z.infer<
  typeof BusinessCaseActivityResponseBodySchema
>;
export type GetBusinessCaseActivityListApiResponse =
  /** status 200  */ BusinessCaseActivityResponseBody[];
export type GetBusinessCaseActivityListApiArg = {
  businessCaseId: string;
};
export type GetVehicleActivityListApiResponse = GetBusinessCaseActivityListApiResponse;
export type GetVehicleActivityListApiArg = {
  vehicleId: string;
};
export type CreateSaleBusinessCaseApiResponse = /** status 201  */ UuidResponseBodyBusinessCase;
export type CreateSaleBusinessCaseApiArg = {
  createSaleBusinessCaseRequestBody: CreateSaleBusinessCaseRequestBody;
  // Optional explicit X-Branch override (branch confirmed on the vehicle card). Falls back to active branch.
  xBranch?: string;
};
export type GetSalePresetSettingsListApiResponse =
  /** status 200  */ GetSalePresetSettingsListItemResponseBody[];
export type GetSalePresetSettingsListApiArg = void;
export const GetSalePresetSettingsListItemResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  makes: z.array(z.string()),
});
export type GetSalePresetSettingsListItemResponseBody = z.infer<
  typeof GetSalePresetSettingsListItemResponseBodySchema
>;
export type GetSalePresetSettingsApiResponse = /** status 200  */ GetSalePresetSettingsResponseBody;
export type GetSalePresetSettingsApiArg = {
  salePresetId: string;
};
export const InterestActivityResponseBodySchema = z.object({
  id: z.string(),
  customerMessageId: z.string().nullable().optional(),
  relatedResourceId: z.string().nullable().optional(),
  businessCaseId: z.string().nullable(),
  businessCaseCode: z.string().nullable(),
  businessCaseType: z.string().nullable(),
  subject: z.string().nullable(),
  activityType: SalesActivityTypeEnumSchema,
  userId: z.string().nullable(),
  userName: z.string(),
  timestamp: z.string(),
  description: z.string().nullable(),
  relatedTask: SalesActivityRelatedTaskResponseBodySchema.nullable(),
  relatedMessage: SalesActivityRelatedMessageResponseBodySchema.nullable(),
  relatedCall: SalesActivityRelatedCallResponseBodySchema.nullable(),
  reasonCloseCodeId: z.string().nullable(),
  reasonCloseNote: z.string().nullable(),
  isEditable: z.boolean(),
  relatedChatConversation: SalesActivityRelatedChatConversationResponseBodySchema.nullable(),
  relatedTestDrive: SalesActivityRelatedTestDriveResponseBodySchema.nullable(),
});
export type InterestActivityResponseBody = z.infer<typeof InterestActivityResponseBodySchema>;
export type CreateInterestActivityApiResponse = /** status 201  */ UuidResponseBody;
export type CreateInterestActivityApiArg = {
  interestId: string;
  createSalesActivityRequestBody: CreateSalesActivityRequestBody;
};

export type GetInterestActivityListApiResponse = /** status 200  */ InterestActivityResponseBody[];
export type GetInterestActivityListApiArg = {
  interestId: string;
};
export const SalesActivityManualTypeEnumSchema = z.enum([
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
]);
export type SalesActivityManualTypeEnum = z.infer<typeof SalesActivityManualTypeEnumSchema>;
export const PatchSalesActivityRequestBodySchema = z.object({
  createdAt: z.string(),
  activityType: SalesActivityManualTypeEnumSchema,
  description: z.string().nullable(),
  subject: z.string().nullable(),
});
export type PatchSalesActivityRequestBody = z.infer<typeof PatchSalesActivityRequestBodySchema>;
export const CreateSalesActivityRequestBodySchema = z.object({
  activityType: SalesActivityManualTypeEnumSchema,
  createdAt: z.string(),
  description: z.string().nullable(),
  subject: z.string().nullable(),
});
export type CreateSalesActivityRequestBody = z.infer<typeof CreateSalesActivityRequestBodySchema>;
export type CreateBusinessCaseActivityApiResponse = /** status 201  */ UuidResponseBody;
export type CreateBusinessCaseActivityApiArg = {
  businessCaseId: string;
  createSalesActivityRequestBody: CreateSalesActivityRequestBody;
};
export type GetSalePresetSettingsResponseBody = {
  id: string;
  name: string;
  makes: string[];
  watermarkId: string | null;
  backgroundRemovalId: string | null;
  promotionalPhotoId: string | null;
  digitalCertificateCode: string | null;
};
export type CreateSalePresetSettingsApiResponse = /** status 201  */ UuidResponseBody;
export type CreateSalePresetSettingsApiArg = {
  createSalePresetSettingsRequestBody: CreateSalePresetSettingsRequestBody;
};
export type DeleteSalePresetSettingsApiResponse = unknown;
export type DeleteSalePresetSettingsApiArg = {
  salePresetId: string;
};
export type UpdateSalePresetSettingsApiResponse = unknown;
export type UpdateSalePresetSettingsApiArg = {
  salePresetId: string;
  updateSalePresetSettingsRequestBody: UpdateSalePresetSettingsRequestBody;
};
export type CreateSalePresetSettingsRequestBody = {
  name: string;
  makes: string[];
  watermarkId: string | null;
  backgroundRemovalId: string | null;
  promotionalPhotoId: string | null;
  digitalCertificateCode: string | null;
};
export type UpdateSalePresetSettingsRequestBody = {
  name: string;
  makes: string[];
  watermarkId: string | null;
  backgroundRemovalId: string | null;
  promotionalPhotoId: string | null;
  digitalCertificateCode: string | null;
};
export type CreateSalePresetSettingsV2ApiResponse = /** status 201  */ UuidResponseBody;
export type CreateSalePresetSettingsV2ApiArg = {
  createSalePresetSettingsV2RequestBody: CreateSalePresetSettingsV2RequestBody;
};
export type DeleteSalePresetSettingsV2ApiResponse = unknown;
export type DeleteSalePresetSettingsV2ApiArg = {
  salePresetId: string;
  deleteSalePresetSettingsV2RequestBody: DeleteSalePresetSettingsV2RequestBody;
};
export type GetSalePresetSettingsV2ApiResponse =
  /** status 200  */ GetSalePresetSettingsV2ResponseBody;
export type GetSalePresetSettingsV2ApiArg = {
  salePresetId: string;
};
export type UpdateSalePresetSettingsV2ApiResponse = unknown;
export type UpdateSalePresetSettingsV2ApiArg = {
  salePresetId: string;
  updateSalePresetSettingsV2RequestBody: UpdateSalePresetSettingsV2RequestBody;
};
export const QualitySealStatusSchema = z.enum(['ACTIVE', 'DISCONTINUED']);
export type QualitySealStatus = z.infer<typeof QualitySealStatusSchema>;
export const QualitySealResponseBodySchema = z.object({
  key: z.string(),
  displayName: z.string(),
  status: QualitySealStatusSchema,
});
export type QualitySealResponseBody = z.infer<typeof QualitySealResponseBodySchema>;
export const ListQualitySealResponseBodySchema = QualitySealResponseBodySchema;
export type ListQualitySealResponseBody = z.infer<typeof ListQualitySealResponseBodySchema>;
export type GetQualitySealsApiResponse = ListQualitySealResponseBody[];
export type GetQualitySealsApiArg = {
  makeKeys?: string[];
  vehicleType?: string;
};
export const GetSalePresetQualitySealImpactResponseBodySchema = z.object({
  eligibleVehicleCount: z.number(),
  manualVehicleCount: z.number(),
});
export type GetSalePresetQualitySealImpactResponseBody = z.infer<
  typeof GetSalePresetQualitySealImpactResponseBodySchema
>;
export type GetSalePresetQualitySealImpactApiResponse = GetSalePresetQualitySealImpactResponseBody;
export type GetSalePresetQualitySealImpactApiArg = {
  salePresetId: string;
  defaultQualitySealKey?: string | null;
};
export const SalesPatchBusinessCaseSourceCodeIdRequestBodySchema = z.object({
  sourceCodeId: z.string().nullable(),
});
export type SalesPatchBusinessCaseSourceCodeIdRequestBody = z.infer<
  typeof SalesPatchBusinessCaseSourceCodeIdRequestBodySchema
>;
export type SalesPatchBusinessCaseSourceCodeIdApiResponse = unknown;
export type SalesPatchBusinessCaseSourceCodeIdApiArg = {
  businessCaseId: string;
  patchBusinessCaseSourceCodeIdRequestBody: SalesPatchBusinessCaseSourceCodeIdRequestBody;
};
export const CreateSalePresetSettingsV2RequestBodySchema = z.object({
  name: z.string(),
  makes: z.array(z.string()),
  watermarkId: z.string().nullable(),
  backgroundRemovalId: z.string().nullable(),
  promotionalPhotoId: z.string().nullable(),
  digitalCertificateThemeId: z.string().nullable(),
  warrantyProgramId: z.string().nullable().optional(),
  applyToVehicles: z.boolean().optional(),
  defaultQualitySealKey: z.string().nullable().optional(),
  propagateQualitySeal: z.boolean().optional(),
});
export type CreateSalePresetSettingsV2RequestBody = z.infer<
  typeof CreateSalePresetSettingsV2RequestBodySchema
>;
export const DeleteSalePresetSettingsV2RequestBodySchema = z.object({
  replacementId: z.string().nullable().optional(),
});
export type DeleteSalePresetSettingsV2RequestBody = z.infer<
  typeof DeleteSalePresetSettingsV2RequestBodySchema
>;
export const GetSalePresetSettingsV2ResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  makes: z.array(z.string()),
  watermarkId: z.string().nullable(),
  backgroundRemovalId: z.string().nullable(),
  promotionalPhotoId: z.string().nullable(),
  digitalCertificateThemeId: z.string().nullable(),
  warrantyProgramId: z.string().nullable(),
  defaultQualitySealKey: z.string().nullable(),
  defaultQualitySeal: QualitySealResponseBodySchema.nullable(),
});
export type GetSalePresetSettingsV2ResponseBody = z.infer<
  typeof GetSalePresetSettingsV2ResponseBodySchema
>;
export const UpdateSalePresetSettingsV2RequestBodySchema = z.object({
  name: z.string(),
  makes: z.array(z.string()),
  watermarkId: z.string().nullable(),
  backgroundRemovalId: z.string().nullable(),
  promotionalPhotoId: z.string().nullable(),
  digitalCertificateThemeId: z.string().nullable(),
  warrantyProgramId: z.string().nullable().optional(),
  applyToVehicles: z.boolean().optional(),
  defaultQualitySealKey: z.string().nullable().optional(),
  propagateQualitySeal: z.boolean().optional(),
});
export type UpdateSalePresetSettingsV2RequestBody = z.infer<
  typeof UpdateSalePresetSettingsV2RequestBodySchema
>;

export type SalesFinanceSettingsResponseBody = {
  defaultDailyInterestRate: BigDecimal | null;
  expectedDaysToSell: number | null;
  dailyStorageCost: BigDecimal | null;
  fixedPurchaseCosts: BigDecimal | null;
};
export type GetSalesFinanceSettingsApiResponse =
  /** status 200  */ SalesFinanceSettingsResponseBody;

export const SalesFinanceSettingsRequestBodySchema = z.object({
  defaultDailyInterestRate: BigDecimalSchema.nullable(),
  expectedDaysToSell: z.number().min(0).nullable(),
  dailyStorageCost: BigDecimalSchema.nullable().optional(),
  fixedPurchaseCosts: BigDecimalSchema.nullable().optional(),
});
export type SalesFinanceSettingsRequestBody = z.infer<typeof SalesFinanceSettingsRequestBodySchema>;
export type SetSalesFinanceSettingsApiResponse = unknown;
export type SetSalesFinanceSettingsApiArg = {
  salesFinanceSettingsRequestBody: SalesFinanceSettingsRequestBody;
};

export type UpdateKeyLocationApiResponse = unknown;
export type UpdateKeyLocationApiArg = {
  vehicleId: string;
  updateVehicleKeyLocationRequestBody: UpdateVehicleKeyLocationRequestBody;
};
export type UuidResponseBodyBusinessCase = {
  id: string;
};
export type PurchaseBusinessCaseType = 'IN_STOCK' | 'BROKERAGE';
export type CreatePurchaseBusinessCaseRequestBody = {
  customerId: string;
  vehicleToPurchaseId: string;
  purchaseType: PurchaseBusinessCaseType;
  createdAt?: string | null;
};
export type CreateSaleBusinessCaseRequestBody = {
  customerId: string;
  vehicleToSaleId: string;
  createdAt?: string | null;
  sourceCodeId?: string | null;
};
export type UpdateVehicleKeyLocationRequestBody = {
  keyLocation: string | null;
};
export type WindshieldContactEmailAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type WindshieldContactPhoneAttribute = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: PhoneNumber | null;
  defaultValue?: PhoneNumber | null;
  displayValue?: PhoneNumber | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type WindshieldUrl = {
  code: VehicleAdvertisingSettingsCodeEnum;
  type?: VehicleAdvertisingSettingsTypeEnum;
  title?: string;
  value: string | null;
  defaultValue?: string | null;
  displayValue?: string | null;
  extra?: object | null;
  required?: false;
  override: boolean;
  hasPublishControl?: boolean;
  publish: boolean;
};
export type VehicleWindshieldAdvertisingAttributeResponseBody =
  | CommonTitleExtensionAttribute
  | CommonMileageAttribute
  | WindshieldContactEmailAttribute
  | WindshieldContactPhoneAttribute
  | CommonDescriptionAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | WindshieldUrl;
export type VehicleWindshieldAdvertisingSettingsResponseBody = {
  attributes: VehicleWindshieldAdvertisingAttributeResponseBody[];
  updatedAt: string | null;
  updatedBy: string | null;
};
export type VehicleWindshieldAdvertisingAttributeRequestBody =
  | CommonTitleExtensionAttribute
  | CommonMileageAttribute
  | WindshieldContactEmailAttribute
  | WindshieldContactPhoneAttribute
  | CommonDescriptionAttribute
  | CommonPriceAttribute
  | CommonFinancingPriceAttribute
  | CommonPriceNoteAttribute
  | WindshieldUrl;
export type PatchVehicleWindshieldAdvertisingSettingsRequestBody = {
  attributes?: VehicleWindshieldAdvertisingAttributeRequestBody[] | null;
};
export type EditPlatformSettingsApiResponse =
  /** status 200  */ VehiclePlatformAdvertisingSettingsResponseBody;
export type EditPlatformSettingsApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  vehiclePlatformAdvertisingSettingsRequestBody: VehiclePlatformAdvertisingSettingsRequestBody;
};
export type GetPlatformSettingsApiResponse =
  /** status 200  */ VehiclePlatformAdvertisingSettingsResponseBody;
export type GetPlatformSettingsApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
};

export type DisablePublishCebiaSmartCodeUrlApiResponse = unknown;
export type DisablePublishCebiaSmartCodeUrlApiArg = {
  vehicleId: string;
};

export type EnablePublishCebiaSmartCodeUrlApiResponse = unknown;
export type EnablePublishCebiaSmartCodeUrlApiArg = {
  vehicleId: string;
};

export type PublishCebiaSmartCodeUrlResponseBody = {
  publishCebiaSmartCodeUrl: boolean;
};
export type GetCebiaSmartCodeUrlConfigApiResponse =
  /** status 200  */ PublishCebiaSmartCodeUrlResponseBody;
export type GetCebiaSmartCodeUrlConfigApiArg = {
  vehicleId: string;
};

export type GetGenerationsApiResponse = /** status 200  */ GenerationResponseBody[];
export type GetGenerationsApiArg = {
  vehicleId: string;
};
export type GetVersionsApiResponse = /** status 200  */ VersionResponseBody[];
export type GetVersionsApiArg = {
  vehicleId: string;
};
export type GenerationResponseBody = {
  code: string;
  name: string;
};
export type VersionResponseBody = {
  code: string;
  name: string;
};

export type GetDigitalCertificateThemesApiResponse =
  /** status 200  */ DigitalCertificateThemeResponseBody[];
export type DigitalCertificateThemeResponseBody = {
  id: string;
  name: string;
};

export type ActiveQuickPresetResponseBody = {
  id: string;
  name: string;
};
export type GetActiveQuickPresetsApiResponse = /** status 200  */ ActiveQuickPresetResponseBody[];
export type GetActiveQuickPresetsApiArg = {
  vehicleId: string;
};
export type DisablePlatformForVehicleApiResponse = unknown;
export type DisablePlatformForVehicleApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
};
export type EnablePlatformForVehicleApiResponse = unknown;
export type EnablePlatformForVehicleApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  publishMobileDeViaAdvertisingBridge?: boolean;
  publishVgpViaAdvertisingBridge?: boolean;
  publishOtomotoViaAdvertisingBridge?: boolean;
  publishAutocarisViaAdvertisingBridge?: boolean;
  publishMercedesNaSkladeViaAdvertisingBridge?: boolean;
  publishHyundaiPromiseViaAdvertisingBridge?: boolean;
};
export type RepeatLastActionApiResponse = unknown;
export type RepeatLastActionApiArg = {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
};
export type TemplateListItemResponseBody = {
  id: string;
  title: string;
  note: string | null;
  fileName: string;
  primary: boolean;
};
export type RenderedTemplateResponseBody = {
  url: string;
  pdfUrl: string;
};
export type WindshieldPaperRequestBody = {
  templateId: string;
  vehicleId: string;
};
export type GetWindshieldPaperTemplatesApiResponse =
  /** status 200  */ TemplateListItemResponseBody[];
export type GetWindshieldPaperTemplatesApiArg = {};
export type RenderWindshieldPaperDocumentApiResponse =
  /** status 200  */ RenderedTemplateResponseBody;
export type RenderWindshieldPaperDocumentApiArg = {
  windshieldPaperRequestBody: WindshieldPaperRequestBody;
};
export type GenerateVgpCertificateApiArg = {
  vehicleId: string;
};
